import {
	type FieldsAttachmentModel,
	type FieldValuesAttachmentModel,
	type FieldValuesModel,
	toUuidArray,
	type UUID,
} from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { type FieldFilesAndPromises, type FieldSchemaConfig, UUIDFile, UUIDPromise } from "@overmap-ai/forms"
import { cloneDeep } from "lodash"

import { AppSDK, sdk } from "../../sdk"
import type { RootState } from "../../store"

export const getFieldFilesAndPromisesFromAtachments = <TAttachment extends UUID<FieldsAttachmentModel>>(
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>,
) => {
	const fieldFilesAndPromises: FieldFilesAndPromises = {}

	for (const attachment of attachments) {
		const promise = UUIDPromise.from(
			service
				.getPresignedUrl(attachment.uuid)
				.then((fileUrl) => sdk.files.fetchFileFromUrl(fileUrl, attachment.file_sha1, attachment.file_name))
				.then((file) => UUIDFile.from(attachment.uuid, file)),
			attachment.uuid,
		)

		if (!fieldFilesAndPromises[attachment.field_identifier]) fieldFilesAndPromises[attachment.field_identifier] = []

		fieldFilesAndPromises[attachment.field_identifier].push(promise)
	}

	return fieldFilesAndPromises
}

export const getFieldImagesAndPromisesFromAttachments = <TAttachment extends UUID<FieldsAttachmentModel>>(
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>,
) => {
	const filePromiseMapping: Record<string, Promise<File> | File> = {}

	for (const attachment of attachments) {
		filePromiseMapping[attachment.field_identifier] = service
			.getPresignedUrl(attachment.uuid)
			.then((fileUrl) => sdk.files.fetchFileFromUrl(fileUrl, attachment.file_sha1, attachment.file_name))
	}

	return filePromiseMapping
}

export const insertImagesAndPromisesToFields = (
	fieldSchema: FieldSchemaConfig,
	fieldImagesAndPromises: Record<string, Promise<File> | File>,
): FieldSchemaConfig => {
	const fieldsCopy: FieldSchemaConfig = cloneDeep(fieldSchema)

	for (let sectionIndex = 0; sectionIndex < fieldsCopy.length; sectionIndex += 1) {
		const field = fieldsCopy[sectionIndex]

		for (let fieldIndex = 0; fieldIndex < field.fields.length; fieldIndex += 1) {
			const innerField = field.fields[fieldIndex]
			fieldsCopy[sectionIndex].fields[fieldIndex]!.image = fieldImagesAndPromises[innerField.id]
		}
	}

	return fieldsCopy
}

export const getIsFieldValuesDraft = <TFieldValuesModel extends FieldValuesModel>(
	fieldValues: TFieldValuesModel | undefined,
): fieldValues is TFieldValuesModel => {
	if (!fieldValues) return false

	return !fieldValues.published_at
}

export const getAttachmentPayloadsFromFilesAndPromises = (
	fieldValuesId: string,
	filesAndPromises: FieldFilesAndPromises,
	fieldValueAttachments: UUID<FieldValuesAttachmentModel>[],
) => {
	const fieldValuesAttachmentsMapping: Record<
		UUID<FieldValuesAttachmentModel>["uuid"],
		UUID<FieldValuesAttachmentModel>
	> = {}

	for (const fieldValuesAttachment of fieldValueAttachments) {
		fieldValuesAttachmentsMapping[fieldValuesAttachment.uuid] = fieldValuesAttachment
	}

	return Object.entries(filesAndPromises).flatMap(([fieldId, filesOrPromises]) =>
		filesOrPromises.map((fileOrPromise) => {
			if (fileOrPromise instanceof UUIDPromise) {
				if (!(fileOrPromise.uuid in fieldValuesAttachmentsMapping)) {
					throw new Error(
						`Expected ${fileOrPromise.uuid} to be contained within fieldValuesAttachmentsMapping`,
					)
				}

				const { object_url, file_type, file_extension, file_name, file_sha1 } =
					fieldValuesAttachmentsMapping[fileOrPromise.uuid]

				return {
					field_values: fieldValuesId,
					field_identifier: fieldId,
					file_sha1: file_sha1,
					file_extension: file_extension,
					file_name: file_name,
					file_type: file_type,
					object_url: object_url,
				}
			} else {
				return {
					file: fileOrPromise,
					field_identifier: fieldId,
					field_values: fieldValuesId,
				}
			}
		}),
	)
}

export const getFieldValuesAttachmentsByFieldId = <TModel extends FieldValuesAttachmentModel>(
	fieldValuesAttachments: TModel[],
) => {
	const ret = {} as Record<TModel["field_identifier"], TModel[]>

	for (const fieldValuesAttachment of fieldValuesAttachments) {
		const { field_identifier } = fieldValuesAttachment
		if (!(field_identifier in ret)) ret[field_identifier] = []
		ret[field_identifier]!.push(fieldValuesAttachment)
	}

	return ret
}

export const getFieldValuesAttachmentsToDelete = <TModel extends UUID<FieldValuesAttachmentModel>>(
	fieldValuesAttachments: TModel[],
	fieldFilesAndPromises: FieldFilesAndPromises,
) => {
	const filesOrPromisesUuids = new Set(
		Object.values(fieldFilesAndPromises).flatMap((filesOrPromises) => filesOrPromises.map(({ uuid }) => uuid)),
	)

	return fieldValuesAttachments.filter(({ uuid }) => !filesOrPromisesUuids.has(uuid))
}

export const getFieldFilesAndPromisesToCreate = <TModel extends UUID<FieldValuesAttachmentModel>>(
	fieldValuesAttachments: TModel[],
	fieldFilesAndPromises: FieldFilesAndPromises,
) => {
	const previousAttachmentUuids = new Set(toUuidArray(fieldValuesAttachments))

	const filesAndPromisesToCreate: FieldFilesAndPromises = {}

	for (const [fieldId, filesOrPromises] of Object.entries(fieldFilesAndPromises)) {
		const filesOrPromisesToCreate: (UUIDFile | UUIDPromise<UUIDFile>)[] = []

		for (const fileOrPromise of filesOrPromises) {
			if (previousAttachmentUuids.has(fileOrPromise.uuid)) continue
			filesOrPromisesToCreate.push(fileOrPromise)
		}

		if (filesOrPromisesToCreate.length === 0) continue
		filesAndPromisesToCreate[fieldId] = filesOrPromisesToCreate
	}

	return filesAndPromisesToCreate
}
